// Binding a Marvelous garment to a body's skeleton — the pure half.
//
// A garment exported from Marvelous Designer is a DRAPED STATIC MESH: it has
// POSITION, NORMAL and TEXCOORD_0 and no skin at all (measured on Sara's
// isaac_tshirtJoggers exports, ENG-1108: `skins: 0`, no JOINTS_0/WEIGHTS_0 on
// any of its 36 pieces). To ride the body it needs weights, and the body it
// was draped on already has them — so the weights are TRANSFERRED: each
// garment vertex takes the influences of the body vertex nearest to it at the
// bind pose. That is what a DCC's "copy skin weights" does, and it is what
// makes the garment follow the skeleton with no solver and no per-frame cost:
// three skins it like any other SkinnedMesh.
//
// Two things make a garment different from the hair strands that already use
// this machinery (hairStrandSkin.js), and both are encoded below:
//
//   1. FABRIC STANDS OFF THE SKIN. A hair root sits on the scalp, so 5 cm is a
//      generous threshold there. A loose hem hangs clear of the leg, and at
//      5 cm most of a drape would be declared "not on this body" and pinned to
//      a fallback bone. Hence GARMENT_MAX_BIND_DIST.
//   2. A GARMENT HAS ITS OWN TOPOLOGY. A strand binds one root; a garment binds
//      tens of thousands of vertices, and nearest-vertex transfer SNAPS — a
//      vertex can grab the wrong limb where two are close. Its neighbours know
//      better, so the transferred weights are smoothed over the garment's own
//      edges before they are used.
//
// Known limit, deliberately not solved here: smoothing fixes ISOLATED bad
// binds, not a whole contiguous region that bound to the wrong limb (the
// classic inner-thigh / armpit case, where the nearest vertex is genuinely on
// the other leg). The fix for that is a normal-aware k-nearest search, which
// costs a k-NN query VertexGrid does not have. Measure before building it: the
// report below carries the distance distribution that says whether it matters.
//
// No three.js import here, so node:test drives the maths.

import { bindPointsToSkin } from '../skinBind.js'

/** Influences carried per garment vertex — one vec4 pair, three's default layout. */
export const GARMENT_INFLUENCES = 4

/**
 * Metres. Beyond this a garment vertex has no body under it and rides a
 * fallback bone rigidly. 15 cm is chosen to clear real drape (a loose jogger
 * leg, a hem that flares) while still rejecting a garment authored for a
 * different figure, which misses by far more than that.
 */
export const GARMENT_MAX_BIND_DIST = 0.15

/** Smoothing passes over the garment's own edges. */
export const GARMENT_SMOOTH_PASSES = 3

/**
 * Vertex adjacency from a triangle index, as CSR (`offset`, `neighbours`).
 * A null index is read as consecutive triples, which is what a non-indexed
 * geometry is — every triangle isolated, which is correct rather than clever.
 *
 * @param {ArrayLike<number>|null} index
 * @param {number} vertexCount
 * @returns {{ offset: Uint32Array, neighbours: Uint32Array }}
 */
export function vertexAdjacency(index, vertexCount) {
  const tris = index ? Math.floor(index.length / 3) : Math.floor(vertexCount / 3)
  const at = (i) => (index ? index[i] : i)
  // Count first so the CSR arrays are allocated once. Duplicates are counted
  // and then collapsed in place; a Set per vertex would allocate per vertex.
  const counts = new Uint32Array(vertexCount + 1)
  for (let t = 0; t < tris; t++) {
    for (let e = 0; e < 3; e++) {
      const a = at(t * 3 + e)
      if (a < vertexCount) counts[a] += 2
    }
  }
  const offset = new Uint32Array(vertexCount + 1)
  for (let v = 0; v < vertexCount; v++) offset[v + 1] = offset[v] + counts[v]
  const fill = new Uint32Array(vertexCount)
  const raw = new Uint32Array(offset[vertexCount])
  const push = (u, w) => { if (u < vertexCount && w < vertexCount) raw[offset[u] + fill[u]++] = w }
  for (let t = 0; t < tris; t++) {
    const a = at(t * 3), b = at(t * 3 + 1), c = at(t * 3 + 2)
    push(a, b); push(a, c); push(b, a); push(b, c); push(c, a); push(c, b)
  }
  // Collapse duplicates per vertex, compacting into a second CSR.
  const outOffset = new Uint32Array(vertexCount + 1)
  const out = new Uint32Array(raw.length)
  let w = 0
  for (let v = 0; v < vertexCount; v++) {
    outOffset[v] = w
    const slice = raw.subarray(offset[v], offset[v] + fill[v])
    slice.sort()
    let prev = -1
    for (let i = 0; i < slice.length; i++) {
      if (slice[i] === prev || slice[i] === v) continue
      out[w++] = slice[i]
      prev = slice[i]
    }
  }
  outOffset[vertexCount] = w
  return { offset: outOffset, neighbours: out.subarray(0, w) }
}

/**
 * Laplacian smoothing of skin weights over the garment's own edges, in place.
 *
 * Each pass replaces a vertex's weights with the mean of itself and its
 * neighbours, then keeps the `k` heaviest and renormalises. A vertex with no
 * neighbours is left exactly as it was — an isolated piece (a button, a metal
 * eyelet) has nobody to average with and must not be nudged toward nothing.
 *
 * @param {Float32Array} skinIndex  k per vertex, mutated
 * @param {Float32Array} skinWeight k per vertex, mutated
 * @param {{ offset: Uint32Array, neighbours: Uint32Array }} adj
 * @param {number} [passes]
 * @param {number} [k]
 */
export function smoothSkinWeights(skinIndex, skinWeight, adj, passes = GARMENT_SMOOTH_PASSES, k = GARMENT_INFLUENCES) {
  const n = adj.offset.length - 1
  const acc = new Map()   // bone -> weight, reused per vertex
  for (let pass = 0; pass < passes; pass++) {
    const nextIndex = Float32Array.from(skinIndex)
    const nextWeight = Float32Array.from(skinWeight)
    for (let v = 0; v < n; v++) {
      const s = adj.offset[v], e = adj.offset[v + 1]
      if (e === s) continue
      acc.clear()
      const add = (u) => {
        for (let j = 0; j < k; j++) {
          const wt = skinWeight[u * k + j]
          if (wt <= 0) continue
          const b = skinIndex[u * k + j]
          acc.set(b, (acc.get(b) ?? 0) + wt)
        }
      }
      add(v)
      for (let i = s; i < e; i++) add(adj.neighbours[i])
      // Keep the k heaviest, renormalise.
      const pairs = Array.from(acc.entries()).sort((a, b) => b[1] - a[1]).slice(0, k)
      let total = 0
      for (const [, wt] of pairs) total += wt
      for (let j = 0; j < k; j++) {
        const p = pairs[j]
        nextIndex[v * k + j] = p ? p[0] : 0
        nextWeight[v * k + j] = p && total > 0 ? p[1] / total : 0
      }
      if (total <= 0) { nextIndex[v * k] = 0; nextWeight[v * k] = 1 }
    }
    skinIndex.set(nextIndex)
    skinWeight.set(nextWeight)
  }
}

/**
 * Transfers skin weights from a body onto a garment.
 *
 * `positions` must already be in the BODY's space — scaled out of the file's
 * units (garmentUnits.js) and placed (garmentFit.js). Binding in the wrong
 * space is the failure that looks like a working importer and a garment two
 * metres from the character.
 *
 * @param {object} args
 * @param {ArrayLike<number>} args.positions  garment xyz triples, in body space
 * @param {object} args.grid  body skinned vertices (VertexGrid), bind pose, body space
 * @param {(v:number, outIndex:Float32Array, outWeight:Float32Array, o:number)=>void} args.influencesAt
 * @param {number} args.fallbackBone
 * @param {ArrayLike<number>|null} [args.index]  garment triangle index, for smoothing
 * @param {number} [args.maxDist]
 * @param {number} [args.smoothPasses]  0 disables smoothing
 * @returns {{ skinIndex: Float32Array, skinWeight: Float32Array, dists: Float32Array,
 *             stats: { points:number, bound:number, unbound:number,
 *                      medianDist:number, p95Dist:number, maxDist:number, smoothed:number } }}
 */
export function bindGarment({
  positions, grid, influencesAt, fallbackBone,
  index = null, maxDist = GARMENT_MAX_BIND_DIST, smoothPasses = GARMENT_SMOOTH_PASSES,
}) {
  const bound = bindPointsToSkin({
    points: positions, grid, influencesAt, fallbackBone,
    influences: GARMENT_INFLUENCES, maxDist,
  })
  if (smoothPasses > 0) {
    const adj = vertexAdjacency(index, bound.stats.points)
    smoothSkinWeights(bound.skinIndex, bound.skinWeight, adj, smoothPasses, GARMENT_INFLUENCES)
  }
  return { ...bound, stats: { ...bound.stats, smoothed: smoothPasses } }
}
